<template>
    <div
        class="d-flex flex-column fill-height fill-width overflow-y no-overflow-x"
        :el-class="`verify-kyc-${status}`"
    >
        <!-- Indicateur de chargement initial -->
        <div
            v-if="initialKycLoading"
            class="d-flex flex-column justify-center align-center fill-height"
        >
            <div class="text-center">
                <v-progress-circular
                    indeterminate
                    size="64"
                    width="4"
                    color="primary"
                    class="mb-4"
                />
                <h4 class="text-h6 mb-2">Préparation de la vérification</h4>
                <p class="text-body-2 text--secondary">
                    Récupération de votre session KYC en cours...
                </p>
            </div>
        </div>

        <!-- Section KYC avec design amélioré -->
        <div
            v-else-if="status === 'pending' && kycUrl"
            class="d-flex flex-column justify-center align-center fill-height"
        >
            <div class="eqs-main-container margin-auto">
                <v-row align="center" class="fill-height ma-0">
                    <!-- Colonne principale avec contenu KYC -->
                    <v-col cols="12" md="7" lg="8" class="d-flex flex-column justify-center">
                        <!-- Affichage des erreurs si KYC rejeté -->
                        <div v-if="formatedErrors && formatedErrors.length" class="mb-4">
                            <v-alert
                                v-for="(error, index) in formatedErrors"
                                :key="index"
                                type="warning"
                                outlined
                                dense
                                class="mb-2"
                            >
                                {{ error }}
                            </v-alert>
                        </div>

                        <!-- Titre et description -->
                        <div class="mb-6 text-center">
                            <h3 class="text-h5 mb-3 font-weight-medium">
                                <v-icon
                                    left
                                    :color="
                                        formatedErrors && formatedErrors.length
                                            ? 'warning'
                                            : 'success'
                                    "
                                    class="mr-2"
                                    >mdi-shield-check</v-icon
                                >
                                {{
                                    formatedErrors && formatedErrors.length
                                        ? "Vérification à corriger"
                                        : "Vérification d'identité (KYC)"
                                }}
                            </h3>
                            <p class="text-body-1 text--secondary mb-0">
                                {{
                                    formatedErrors && formatedErrors.length
                                        ? "Votre KYC a été rejeté. Veuillez corriger les erreurs ci-dessus et réuploader vos documents."
                                        : "Votre session de vérification d'identité est prête. Cliquez sur le bouton ci-dessous pour commencer."
                                }}
                            </p>
                        </div>

                        <!-- Boutons d'action -->
                        <div class="d-flex flex-column align-center">
                            <!-- Bouton principal pour ouvrir la session KYC -->
                            <div class="mb-4">
                                <EqsBtn
                                    color="success"
                                    large
                                    :loading="kycUrlLoading"
                                    class="px-8 py-3"
                                    elevation="2"
                                    @click="openKycSession"
                                >
                                    <v-icon left>mdi-open-in-new</v-icon>
                                    Lancer la vérification
                                </EqsBtn>
                            </div>

                            <!-- Boutons secondaires -->
                            <div class="d-flex justify-center align-center flex-wrap">
                                <EqsBtn
                                    color="primary"
                                    outlined
                                    small
                                    :loading="kycUrlLoading"
                                    class="ma-2"
                                    @click="refreshKycUrl"
                                >
                                    <v-icon left small>mdi-refresh</v-icon>
                                    Rafraîchir
                                </EqsBtn>

                                <EqsBtn
                                    :color="copySuccess ? 'success' : 'secondary'"
                                    outlined
                                    small
                                    class="ma-2"
                                    :disabled="kycUrlLoading"
                                    @click="copyKycUrl"
                                >
                                    <v-icon small>{{
                                        copySuccess ? "mdi-check" : "mdi-content-copy"
                                    }}</v-icon>
                                    {{ copySuccess ? "Copié !" : "Copier le lien" }}
                                </EqsBtn>
                            </div>

                            <!-- Indicateur de statut -->
                            <div class="mt-4 text-center">
                                <v-chip
                                    v-if="kycUrlLoading"
                                    color="info"
                                    text-color="white"
                                    small
                                    class="px-3"
                                >
                                    <v-progress-circular
                                        indeterminate
                                        size="16"
                                        width="2"
                                        class="mr-2"
                                    />
                                    Récupération en cours...
                                </v-chip>
                                <v-chip
                                    v-else-if="kycUrl"
                                    color="success"
                                    text-color="white"
                                    small
                                    class="px-3"
                                >
                                    <v-icon small class="mr-1">mdi-check-circle</v-icon>
                                    URL KYC disponible
                                </v-chip>
                                <v-chip
                                    v-else-if="kycUrlError"
                                    color="error"
                                    text-color="white"
                                    small
                                    class="px-3"
                                >
                                    <v-icon small class="mr-1">mdi-alert-circle</v-icon>
                                    {{ kycErrorType || "Erreur" }}
                                </v-chip>
                            </div>
                        </div>
                    </v-col>

                    <!-- Colonne image (masquée sur mobile) -->
                    <v-col
                        cols="12"
                        md="5"
                        lg="4"
                        class="d-none d-md-flex justify-center align-center"
                    >
                        <div class="text-center">
                            <EqsImage
                                contain
                                :src="$universe.image('/images/registration-verify.svg')"
                                class="ma-4"
                                style="max-width: 280px; max-height: 280px"
                                :loading="loading || localLoading"
                            />
                            <div class="mt-3">
                                <v-icon color="success" class="mr-1">mdi-security</v-icon>
                                <span class="text-caption text--secondary">Processus sécurisé</span>
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </div>
        </div>

        <!-- Section KYC terminé avec succès -->
        <div
            v-else-if="status === 'success'"
            class="d-flex flex-column justify-center align-center fill-height"
        >
            <div class="eqs-main-container margin-auto">
                <v-row align="center" class="fill-height ma-0">
                    <!-- Colonne principale avec message de succès -->
                    <v-col cols="12" md="7" lg="8" class="d-flex flex-column justify-center">
                        <div class="text-center">
                            <div class="mb-6">
                                <v-icon color="success" size="80" class="mb-4"
                                    >mdi-check-circle</v-icon
                                >
                                <h3 class="text-h4 mb-3 font-weight-medium text-success">
                                    Vérification terminée !
                                </h3>
                                <p class="text-body-1 text--secondary mb-4">
                                    Votre vérification d'identité (KYC) a été acceptée avec succès.
                                    Vous pouvez maintenant accéder à toutes les fonctionnalités de
                                    la plateforme.
                                </p>
                            </div>

                            <!-- Badge de statut -->
                            <v-chip color="success" text-color="white" large class="px-6 py-2 mb-8">
                                <v-icon left>mdi-shield-check</v-icon>
                                Identité vérifiée
                            </v-chip>

                            <!-- Bouton de retour à l'accueil avec design amélioré -->
                            <div class="d-flex justify-center">
                                <v-btn
                                    color="primary"
                                    x-large
                                    rounded
                                    depressed
                                    class="px-12 py-2 text-none font-weight-medium"
                                    style="height: 56px"
                                    @click="goToHome"
                                >
                                    <v-icon left size="24">mdi-home-variant</v-icon>
                                    <span class="text-h6">Retour à l'accueil</span>
                                </v-btn>
                            </div>

                            <!-- Petit texte d'information optionnel -->
                            <p class="text-caption text--secondary mt-6 mb-0">
                                <v-icon small color="grey" class="mr-1"
                                    >mdi-information-outline</v-icon
                                >
                                Félicitations ! Votre compte est maintenant entièrement activé.
                            </p>
                        </div>
                    </v-col>

                    <!-- Colonne image (masquée sur mobile) -->
                    <v-col
                        cols="12"
                        md="5"
                        lg="4"
                        class="d-none d-md-flex justify-center align-center"
                    >
                        <div class="text-center">
                            <EqsImage
                                contain
                                :src="$universe.image('/images/registration-verify.svg')"
                                class="ma-4"
                                style="max-width: 280px; max-height: 280px"
                                :loading="loading || localLoading"
                            />
                            <div class="mt-3">
                                <v-icon color="success" class="mr-1">mdi-verified</v-icon>
                                <span class="text-caption text-success font-weight-medium"
                                    >Vérification réussie</span
                                >
                            </div>
                        </div>
                    </v-col>
                </v-row>
            </div>
        </div>

        <div v-else-if="!initialKycLoading">
            <div v-if="getData.video" class="d-flex flex-column fill-height" style="padding: 10px">
                <EqsTextDisplay
                    paragraph
                    :value="getData.msg"
                    class="semi-bold margin-auto text-center pa-3 max-height-fit"
                />
                <iframe
                    style="
                        border-radius: 1.625rem;
                        max-width: 700px;
                        margin: auto;
                        height: 400px;
                        box-shadow: 2px 1px 11px rgb(0 0 0 / 50%);
                    "
                    width="100%"
                    height="auto"
                    :src="getData.video"
                    frameborder="0"
                    allow="autoplay; encrypted-media"
                    allowfullscreen
                />
            </div>
            <EqsWindows
                v-else-if="!getData.continue"
                :value.sync="step"
                :cycle="false"
                v-bind="{ items: verifyData.steps }"
            >
                <template #top>
                    <div class="eqs-main-container margin-auto">
                        <v-col
                            justify="center"
                            align="center"
                            class="d-flex justify-center align-center"
                        >
                            <EqsTextDisplay
                                paragraph
                                :value="getData.msg"
                                v-bind="{ loading: loading || localLoading }"
                                class="semi-bold margin-auto"
                            />
                        </v-col>
                    </div>
                </template>
                <template #default="{ item }">
                    <div
                        class="eqs-main-container margin-auto d-flex flex-column"
                        style="height: inherit"
                    >
                        <EqsImage
                            contain
                            class="d-flex"
                            style="height: 80%"
                            v-bind="{ src: item.img, loading: loading || localLoading }"
                        />
                        <div class="d-flex text-center max-height-fit">
                            <EqsTextDisplay
                                paragraph
                                :value="item.txt"
                                v-bind="{ loading: loading || localLoading }"
                                class="semi-bold margin-auto mt-3"
                            />
                        </div>
                        <v-layout column justify-end align-center>
                            <v-flex v-if="item.actionBtn" class="max-height-fit" mb-3>
                                <EqsBtn
                                    color="primary"
                                    :action="item.actionBtn"
                                    v-bind="{ loading: loading || localLoading }"
                                >
                                    {{ item.txtBtn }}
                                </EqsBtn>
                            </v-flex>
                        </v-layout>
                    </div>
                </template>
            </EqsWindows>

            <div v-else class="eqs-main-container margin-auto">
                <EqsViewWithImage
                    class="mx-3"
                    v-bind="{ img: $universe.image('/images/registration-verify.svg') }"
                >
                    <v-row class="px-3">
                        <v-col :cols="12">
                            <EqsTextDisplay
                                paragraph
                                :value="getData.msg"
                                v-bind="{ loading: loading || localLoading }"
                                class="semi-bold margin-auto"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="formatedErrors" class="px-3">
                        <v-col v-for="(error, index) in formatedErrors" :key="index" :cols="12">
                            <EqsAlert
                                v-bind="{
                                    eqsType: 'error',
                                    text: error,
                                    loading: loading || localLoading
                                }"
                            />
                        </v-col>
                    </v-row>
                    <v-row v-if="getData.continue.action" class="px-3">
                        <v-col>
                            <EqsBtn
                                el-class="verify-continue-btn"
                                color="primary"
                                :action="getData.continue.action"
                                v-bind="{ loading: loading || localLoading }"
                            >
                                {{ getData.continue.txt }}
                            </EqsBtn>
                        </v-col>
                    </v-row>
                </EqsViewWithImage>
            </div>
        </div>
    </div>
</template>

<script>
import { KYC_PROCESSING_TIMEOUT, KYC_INTERVAL, PRODUCTION_ENVIRONMENT } from "@/assets/constants";
import { getLabelFromCategory } from "@/util/documents";
import { lemonwayStatusItem, lemonwayStatusError } from "@equisafe-ui-vue/src/globals/lemonway.js";
const verifyRegistrationPoller = "verify-registration";

export default {
    name: "VerifyKyc",
    props: {
        kycType: {
            type: String,
            default: ""
        },
        verifyData: {
            type: Object,
            required: true
        },
        loading: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            kycResult: null,
            status: "",
            step: 0,
            lastIdVerified: null,
            formatedErrors: null,
            localLoading: true,
            // Propriétés pour l'URL KYC
            kycUrl: null,
            kycUrlError: null,
            kycUrlLoading: false,
            kycErrorType: null,
            // Indicateur visuel pour la copie
            copySuccess: false,
            // Indicateur de chargement initial pour éviter d'afficher le composant avant la première récupération
            initialKycLoading: true
        };
    },
    computed: {
        getData() {
            return this.status ? this.verifyData[this.status] : {};
        }
    },
    watch: {
        "$route.params.id": {
            immediate: true,
            async handler(entityId) {
                this.status = "pending";
                if (entityId) await this.getEntityRegistrationState(entityId);
            }
        }
    },
    beforeDestroy() {
        this.$poll.stop(verifyRegistrationPoller);
    },
    methods: {
        /**
         * Rafraîchit l'URL de la session KYC en utilisant l'endpoint existant
         */
        async refreshKycUrl() {
            const entityId = this.$route.params.id;
            if (!entityId) {
                this.$ui.error("Aucune entité sélectionnée");
                return;
            }

            this.kycUrlLoading = true;
            this.kycUrlError = null;
            this.kycErrorType = null;

            try {
                await this.getEntityRegistrationState(entityId);

                if (this.status === "success") {
                    this.kycErrorType = "kyc_completed";
                } else if (this.status === "pending" && this.kycUrl) {
                    // Succès silencieux - l'indicateur visuel suffit
                } else {
                    this.kycUrlError = "Aucune URL KYC disponible";
                    this.kycErrorType = "no_session";
                }
            } catch (error) {
                console.error("Erreur lors du rafraîchissement KYC:", error);
                this.kycUrlError = "Erreur lors de la récupération";
                this.kycErrorType = "api_error";
                // Garder seulement les erreurs critiques
                this.$ui.error("Erreur lors du rafraîchissement de l'URL KYC");
            } finally {
                this.kycUrlLoading = false;
            }
        },

        /**
         * Copie l'URL KYC dans le presse-papiers
         */
        async copyKycUrl() {
            if (!this.kycUrl) {
                this.$ui.error("Aucune URL KYC à copier");
                return;
            }

            try {
                // Utiliser l'API Clipboard moderne si disponible
                if (navigator.clipboard && navigator.clipboard.writeText) {
                    await navigator.clipboard.writeText(this.kycUrl);
                    // Feedback visuel discret
                    this.copySuccess = true;
                    setTimeout(() => {
                        this.copySuccess = false;
                    }, 2000); // Revenir à l'icône normale après 2s
                } else {
                    // Fallback pour les navigateurs plus anciens
                    const textArea = document.createElement("textarea");
                    textArea.value = this.kycUrl;
                    textArea.style.position = "fixed";
                    textArea.style.left = "-9999px";
                    textArea.style.top = "-9999px";
                    document.body.appendChild(textArea);
                    textArea.focus();
                    textArea.select();

                    try {
                        document.execCommand("copy");
                        // Feedback visuel discret
                        this.copySuccess = true;
                        setTimeout(() => {
                            this.copySuccess = false;
                        }, 2000);
                    } catch (err) {
                        console.error("Erreur lors de la copie:", err);
                        this.$ui.error(
                            "Impossible de copier l'URL. Copiez manuellement : " + this.kycUrl
                        );
                    } finally {
                        document.body.removeChild(textArea);
                    }
                }
            } catch (error) {
                console.error("Erreur lors de la copie de l'URL KYC:", error);
                this.$ui.error("Impossible de copier l'URL. Copiez manuellement : " + this.kycUrl);
            }
        },

        /**
         * Ouvre la session KYC dans un nouvel onglet et recharge l'URL en parallèle
         */
        async openKycSession() {
            if (!this.kycUrl) {
                this.$ui.error("Aucune URL KYC disponible");
                return;
            }
            if (this.kycUrlLoading) {
                return;
            }

            this.kycUrlLoading = true;

            try {
                // Ouvrir immédiatement dans un nouvel onglet avec l'URL actuelle
                const newWindow = window.open(this.kycUrl, "_blank");

                if (!newWindow) {
                    return;
                }
                const entityId = this.$route.params.id;
                if (entityId) {
                    try {
                        await this.getEntityRegistrationState(entityId);

                        if (this.kycUrl) {
                            console.log("URL KYC mise à jour après ouverture de session");
                        }
                    } catch (error) {
                        console.error("Erreur lors du rechargement KYC après ouverture:", error);
                        // Ne pas afficher d'erreur à l'utilisateur car la session est déjà ouverte
                    }
                }
            } catch (error) {
                console.error("Erreur lors de l'ouverture de la session KYC:", error);
                this.$ui.error("Impossible d'ouvrir la session KYC");
            } finally {
                // Remettre le bouton disponible après un délai minimum pour éviter les double-clics
                this.kycUrlLoading = false;
            }
        },

        /**
         * Vérifie si le KYC a été complété
         */
        async checkKycCompletion() {
            const entityId = this.$route.params.id;
            if (entityId) {
                try {
                    await this.getEntityRegistrationState(entityId);
                    if (this.status === "success") {
                        // Garder cette notification car c'est un événement important
                        this.$ui.success("KYC complété avec succès !");
                    }
                } catch (error) {
                    console.error("Erreur lors de la vérification du statut KYC:", error);
                }
            }
        },

        /**
         * Redirige vers la page d'accueil
         */
        goToHome() {
            try {
                // Essayer de rediriger vers la route d'accueil
                this.$router.push({ name: "home" });
            } catch (error) {
                console.error("Erreur lors de la redirection vers l'accueil:", error);
                // Fallback: redirection directe
                window.location.href = "/";
            }
        },

        async getEntityVerificationResult(entityId) {
            let result = null;
            try {
                result = await this.$api.getEntityVerificationResult(entityId);
                // await this.$role.updateMyCurrentRole(entityId);
            } catch (e) {
                throw e;
            } finally {
                return {
                    result,
                    entityId
                };
            }
        },
        checkRegistrationState(verif) {
            if (this.localLoading) this.localLoading = false;
            return (
                verif.result && verif.result.data && verif.result.data.lemonway_state !== "pending"
            );
        },
        async verifyKyc(entityId) {
            try {
                const res = await this.$poll.asyncCall(
                    verifyRegistrationPoller,
                    this.getEntityVerificationResult,
                    entityId,
                    verif => this.checkRegistrationState(verif),
                    KYC_INTERVAL,
                    KYC_PROCESSING_TIMEOUT
                );
                return res;
            } catch (e) {
                if (!PRODUCTION_ENVIRONMENT)
                    this.$ui.error(this.$t("ui-error.registration-processing-timeout"));
                throw e;
            }
        },
        async verifyKyb(entityId) {
            let result = null;
            try {
                result = await this.$api.getEntityVerificationResult(entityId);
            } catch (e) {
                throw e;
            } finally {
                this.localLoading = false;
                // Pour une société, on renvoie toujours le résultat : tant que
                // le KYB n'est pas finalisé chez Lemonway (état "pending"), le
                // tunnel doit continuer à exposer le lien KYB (kyc_url) afin de
                // permettre la soumission/reprise des documents. Le lien est de
                // toute façon masqué côté template quand kyc_url est vide.
                return result
                    ? {
                          result,
                          entityId
                      }
                    : null;
            }
        },
        getFormatedErrors(kycRes) {
            let formatedErrors = [];
            if (kycRes.lemonway_reason) {
                formatedErrors.push(
                    `${this.$t("rules.lemonway-reason")}: ${kycRes.lemonway_reason}`
                );
            }
            const getDocsErrors = kycRes.docs
                .filter(doc => {
                    const res = lemonwayStatusItem(this, doc.lemonway_status, doc.lemonway_reason);
                    return res.status === "error";
                })
                .map(errDoc => {
                    const lwError = lemonwayStatusError(
                        this,
                        errDoc.lemonway_status,
                        [errDoc.lemonway_reason, errDoc.comment].filter(msg => !!msg).join(" - ")
                    );
                    const res = `${this.$t("label.error-for")} '${getLabelFromCategory(
                        errDoc.category,
                        errDoc.lemonway_type
                    )}' :\n${lwError}`;
                    return res;
                });
            if (getDocsErrors && getDocsErrors.length) {
                formatedErrors.push(...getDocsErrors);
            }
            return formatedErrors;
        },
        async getEntityRegistrationState(entityId) {
            let registrationRes = null;
            const verifyFn =
                this.$entity.represented().is_company && this.$dev.isProd()
                    ? this.verifyKyb
                    : this.verifyKyc;
            registrationRes = await verifyFn(entityId);
            if (registrationRes) {
                try {
                    // update role to have entity with right status when status changed from pending
                    await this.$role.updateMyCurrentRole(registrationRes.entityId);

                    // Extraire l'URL KYC de la réponse
                    const responseData = registrationRes.result.data;
                    if (responseData.kyc_url) {
                        this.kycUrl = responseData.kyc_url;
                        this.kycUrlError = null;
                        this.kycErrorType = null;
                    } else {
                        this.kycUrl = null;
                        if (responseData.lemonway_state === "success") {
                            this.kycErrorType = "kyc_completed";
                        } else {
                            this.kycErrorType = "no_session";
                        }
                    }

                    const formatedErrors = this.getFormatedErrors(responseData);

                    // Déterminer le statut en fonction de lemonway_state et des erreurs
                    if (responseData.lemonway_state === "success") {
                        this.status = "success";
                    } else if (
                        responseData.lemonway_state === "failure" &&
                        responseData.lemonway_reason === "ONBOARDING_REJECTED"
                    ) {
                        // Si le KYC a été rejeté, on reste en mode "pending" pour permettre
                        // l'accès au tunnel KYC et la possibilité de réuploader des documents
                        this.status = "pending";
                        this.formatedErrors = formatedErrors;
                    } else if (!!formatedErrors && formatedErrors.length) {
                        // Pour les autres cas d'erreur, on affiche le statut "error"
                        this.status = "error";
                        this.formatedErrors = formatedErrors;
                    } else {
                        // Sinon, on est en mode "pending" (en cours de vérification)
                        this.status = "pending";
                    }
                } catch (e) {
                    console.error({ e });
                } finally {
                    // Marquer le chargement initial comme terminé
                    this.initialKycLoading = false;
                }
            } else {
                // Même si pas de résultat, arrêter le chargement initial
                this.initialKycLoading = false;
            }
        }
    }
};
</script>
